import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import getConfig from 'next/config';
import { Client } from '@amplitude/marketing-analytics-browser/lib/esm/typings/browser-client';
import { useRouter } from 'next/router';
import { FdUserType } from '@/types/common';

type SetStateT = React.Dispatch<React.SetStateAction<string>>;
interface FunctionT {
  setPage: SetStateT,
  isActive: boolean,
  init: ()=>Promise<void>,
  logAmplitudeEvent:(
    eventName: string,
    eventProps?: Record<string, string | number>,
    otherAtts?: Record<string, string>,
  ) =>void
}

const AmplitudeEventContext = createContext<FunctionT | Record<string, never>>({});

export function AmplitudeProvider({ children }:{ children:JSX.Element }) {
  const [amplitude, setAmplitude] = useState<Client>();

  const init = useCallback(async () => {
    if (amplitude) {
      return;
    }
    const { publicRuntimeConfig } = getConfig();
    const amplitudeInit = (await import('@amplitude/marketing-analytics-browser')).createInstance();
    amplitudeInit.init(publicRuntimeConfig.AMP_KEY, undefined, {
      pageViewTracking: { trackOn: () => false },
      attribution: { resetSessionOnNewCampaign: true },
    });
    setAmplitude(amplitudeInit);
  }, [amplitude]);
  useEffect(() => {
    init();
  }, [init]);

  const [pageName, setPage] = useState<string>();
  const [prevPage, setPrevPage] = useState<string[]>([]);
  const { asPath } = useRouter();
  useEffect(() => {
    if (prevPage.length === 0) {
      setPrevPage(() => [window.location.href]);
    }
  }, [prevPage]);
  useEffect(() => {
    if (window.location.href) setPrevPage((prev) => [window.location.href, ...prev]);
  }, [asPath]);

  const logAmplitudeEvent = useCallback((
    eventName: string,
    eventProps?: Record<string, string | number>,
    otherAtts?: Record<string, string>,
  ) => {
    const dfsLoggedInState = localStorage.getItem('FD_USER');
    const dfsUserId = dfsLoggedInState
      ? JSON.parse(dfsLoggedInState) as FdUserType
      : undefined;

    const defaultProperties = {
      'Page Path': window.location.pathname,
      'Full URL': window.location.href,
      Hostname: window.location.hostname,
      'Page Title': document?.title,
      Vertical: 'content',
      Product: 'research_articles',
      'Page Name': otherAtts?.page || pageName,
      user_id: dfsLoggedInState ? dfsUserId?.id : 'logged_out',
      'account type': dfsLoggedInState ? 'DFS' : '',
      'Previous Page': prevPage?.at(0),
    };

    const properties = eventProps;
    amplitude?.logEvent(eventName, { ...defaultProperties, ...properties });
  }, [amplitude, pageName, prevPage]);

  const functions = useMemo(() => ({
    logAmplitudeEvent,
    setPage,
    init,
    isActive: !!amplitude,
  }), [logAmplitudeEvent, amplitude, init]) as FunctionT;
  return (
    <AmplitudeEventContext.Provider value={functions}>
      {children}
    </AmplitudeEventContext.Provider>
  );
}

export const useAmplitude = () => useContext(AmplitudeEventContext);
